/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

 
 (async function () {
  const params = new URLSearchParams(window.location.search);
  const offerId = params.get('offer');

  const offers = {
    'key-pink': {
      variantId: 45115067629833,
      discountCode: 'WELCOMEKEY'
    },

    'key-green': {
      variantId: 45115067564297,
      discountCode: 'WELCOMEKEY'
    },

    'key-yellow': {
      variantId: 45115067597065,
      discountCode: 'WELCOMEKEY'
    },

    'key-neon-pink': {
      variantId: 45115067531529,
      discountCode: 'WELCOMEKEY'
    },

    'key-turkis': {
      variantId: 45115067662601,
      discountCode: 'WELCOMEKEY'
    },

    'key-black': {
      variantId: 45115067695369,
      discountCode: 'WELCOMEKEY'
    },
    'smile-mango': {
      variantId: 56786179227913,
      discountCode: 'GratisMeSmile'
    },
    'smile-jade': {
      variantId: 56786179195145,
      discountCode: 'GratisMeSmile'
    },
    'smile-sunset': {
      variantId: 56786178933001,
      discountCode: 'GratisMeSmile'
    },
    'smile-black': {
      variantId: 56786179260681,
      discountCode: 'GratisMeSmile'
    },
    'me-leo-berry': {
      variantId: 56786173559049,
      discountCode: 'GratisMe'
    },
    'me-leo-circus': {
      variantId: 56786173591817,
      discountCode: 'GratisMe'
    },
    'me-leo-plumy': {
      variantId: 56786173624585,
      discountCode: 'GratisMe'
    },
    'me-frida-oranges': {
      variantId: 57098868916489,
      discountCode: 'GratisMe'
    },
    'me-frida-folk': {
      variantId: 57098848076041,
      discountCode: 'GratisMe'
    },
    'me-frida-geo': {
      variantId: 57098874585353,
      discountCode: 'GratisMe'
    },
    'me-squeezy-lemon': {
      variantId: 55610988757257,
      discountCode: 'GratisMe'
    },
    'me-embro-fun': {
      variantId: 56785689379081,
      discountCode: 'GratisMe'
    },
    'me-frida-nature-folk': {
      variantId: 57098848076041,
      discountCode: 'GratisMe'
    },
    'me-frida-jungle': {
      variantId: 57098875699465,
      discountCode: 'GratisMe'
    },
    'me-frida-heads': {
      variantId: 57140254179593,
      discountCode: 'GratisMe'
    },
    'lea-bag-espresso': {
      variantId: 56381111697673,
      discountCode: 'GratisLeaBag'
    },
    'lea-bag-mocca': {
      variantId: 56381111664905,
      discountCode: 'GratisLeaBag'
    },
    'lea-bag-red': {
      variantId: 56381111795977,
      discountCode: 'GratisLeaBag'
    },
    'lea-bag-grey': {
      variantId: 56381111632137,
      discountCode: 'GratisLeaBag'
    },
    'lea-bag-pink': {
      variantId: 56381111828745,
      discountCode: 'GratisLeaBag'
    },
    'lea-bag-blue-grey': {
      variantId: 56381111763209,
      discountCode: 'GratisLeaBag'
    },
    'lea-bag-olive': {
      variantId: 56381111730441,
      discountCode: 'GratisLeaBag'
    },
    'lea-bag-black': {
      variantId: 56381110976777,
      discountCode: 'GratisLeaBag'
    }
  };

  if (!offerId || !offers[offerId]) {
    return;
  }

  const offer = offers[offerId];

  // Wichtig:
  // DE    -> "/"
  // CH    -> "/de-ch/"
  const root = window.Shopify?.routes?.root || '/';

  console.log('Offer:', offerId);
  console.log('Shopify root:', root);
  console.log('Variant:', offer.variantId);

  try {
    // 1. Produkt im AKTUELLEN Markt hinzufügen
    const addResponse = await fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        items: [{
          id: offer.variantId,
          quantity: 1
        }]
      })
    });

    const addResult = await addResponse.json();

    console.log('Cart add status:', addResponse.status);
    console.log('Cart add result:', addResult);

    if (!addResponse.ok) {
      console.error('Produkt konnte nicht hinzugefügt werden:', addResult);
      return;
    }

    // 2. Discount im aktuellen Market aktivieren
    const discountUrl =
      root +
      'discount/' +
      encodeURIComponent(offer.discountCode) +
      '?redirect=' +
      encodeURIComponent(root);

    console.log('Discount URL:', discountUrl);

    // 3. Merken, dass nach dem Redirect der Drawer geöffnet werden soll

    sessionStorage.setItem('openCartDrawerAfterRedirect', '1');
    
    // 4. Shopify übernimmt Discount + Redirect
    window.location.replace(discountUrl);

  } catch (error) {
    console.error('Welcome Offer Error:', error);
  }
})();
(function () {

  function openCartDrawerAfterRedirect() {

    // Nur ausführen, wenn zuvor ein Gratisartikel hinzugefügt wurde

    if (sessionStorage.getItem('openCartDrawerAfterRedirect') !== '1') {

      return;

    }

    // Flag direkt entfernen, damit der Drawer nicht erneut aufgeht

    sessionStorage.removeItem('openCartDrawerAfterRedirect');

    // Kurz warten, damit Shopify und das Theme initialisiert sind

    setTimeout(function () {

      // Warenkorb aktualisieren

      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {

        bubbles: true

      }));

      // Anschliessend Drawer öffnen

      setTimeout(function () {

        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {

          bubbles: true,

          detail: { opener: true }

        }));

      }, 400);

    }, 600);

  }

  if (document.readyState === 'loading') {

    document.addEventListener('DOMContentLoaded', openCartDrawerAfterRedirect);

  } else {

    openCartDrawerAfterRedirect();

  }

})();